import { useState, useEffect } from "react";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import Loader from "../../../../components/Loader/Loader";
import CustomToolbar from "../../../../components/toolbar";
import { BadgesServices } from "../../../../services/challenge_services";
import { Breadcrumbs, Link, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import classes from "../../Challenge.module.css";
import AUTH_CONTEXT from "../../../../store";
import { BASE_PATH_TO_ADD_BADGES, BASE_PATH_TO_UPDATE_BADGES } from "../../../../base_routes_paths/challenge_paths";

const BadgesList = () => {
    const navigate = useNavigate();
    const { token, fileName } = AUTH_CONTEXT();
    const EXPORT_FILENAME = `${fileName}_Badges`;

    const columns = [
        {
            field: "BadgeName",
            headerName: "Badge Name",
            width: 400,
            renderCell: (params) => {
                const BadgeName = params.row.BadgeName;
                return (
                    <table style={{ textAlign: "center" }}>
                        <thead>
                            <tr>
                                <th style={{ paddingRight: 25 }}>Locale</th>
                                <th style={{ paddingRight: 25 }}>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(BadgeName).map(([key, value]) => (
                                <tr key={key}>
                                    <td style={{ paddingRight: 25 }}>{key}</td>
                                    <td style={{ paddingRight: 25 }}>{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                );
            },
        },
        {
            field: "BadgeDescription",
            headerName: "Badge Description",
            width: 400,
            renderCell: (params) => {
                const BadgeDescription = params.row.BadgeDescription;
                return (
                    <table style={{ textAlign: "center" }}>
                        <thead>
                            <tr>
                                <th style={{ paddingRight: 25 }}>Locale</th>
                                <th style={{ paddingRight: 25 }}>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(BadgeDescription).map(([key, value]) => (
                                <tr key={key}>
                                    <td style={{ paddingRight: 25 }}>{key}</td>
                                    <td style={{ paddingRight: 25 }}>{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                );
            },
        },
        {
            field: "BadgeAchievedImageUrl",
            headerName: "Achieved Image URL",
            width: 300,
            renderCell: (params) => {
                const link = params.row.BadgeAchievedImageUrl;
                return (
                    <>
                        <ContentCopyIcon
                            sx={{ marginRight: "10px", cursor: "pointer" }}
                            onClick={() => {
                                navigator.clipboard.writeText(link);
                                toast.success("Copied Link!");
                            }}
                        />
                        <a href={link} target="_blank" rel="noopener noreferrer">
                            {link}
                        </a>
                    </>
                );
            },
        },
        {
            field: "BadgeUnachievedImageUrl",
            headerName: "Unachieved Image URL",
            width: 300,
            renderCell: (params) => {
                const link = params.row.BadgeUnachievedImageUrl;
                return (
                    <>
                        <ContentCopyIcon
                            sx={{ marginRight: "10px", cursor: "pointer" }}
                            onClick={() => {
                                navigator.clipboard.writeText(link);
                                toast.success("Copied Link!");
                            }}
                        />
                        <a href={link} target="_blank" rel="noopener noreferrer">
                            {link}
                        </a>
                    </>
                );
            },
        },
        { field: "BadgeDuration", headerName: "Duration", width: 100 },
        { field: "BadgeCategory", headerName: "Category", width: 150 },
        { field: "BadgeWorkoutID", headerName: "Workout ID", width: 100 },
        { field: "CumulativeRank", headerName: "Cumulative Rank", width: 150 },
        { field: "IsRankBasedCumulative", headerName: "Rank Based", width: 120 },
        { field: "IsAllWorkouts", headerName: "All Workouts", width: 120 },
        { field: "IsActive", headerName: "Active", width: 100 },
    ];

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);

    const fetchResponse = async () => {
        setIsLoading(true);
        try {
            const response = await BadgesServices.getAllBadge(token, navigate, null);
            const formattedData = response.data.map(({ _id: id, ...rest }) => ({ id, ...rest }));
            setData(formattedData);
            toast.success("Data fetched successfully!", { position: "bottom-left", autoClose: 2000 });
        } catch (error) {
            toast.error("Failed to fetch data: " + error.message, { position: "bottom-left", autoClose: 5000 });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchResponse();
    }, []);

    const handleDelete = (id) => {
        BadgesServices.deleteBadges(id, token);
    };

    const actionColumn = [
        {
            field: "action",
            headerName: "Action",
            width: 200,
            renderCell: (params) => {
                return (
                    <div className={classes.cellAction}>
                        <Link
                            href={`${BASE_PATH_TO_UPDATE_BADGES}/` + params.row.id}
                            style={{ textDecoration: "none" }}
                        >
                            <div className={classes.viewButton}>Update</div>
                        </Link>
                        <div
                            className={classes.deleteButton}
                            onClick={() => handleDelete(params.row.id)}
                        >
                            Delete
                        </div>
                    </div>
                );
            },
        },
    ];

    return (
        <div className={classes.datatable}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: 1 }}>
                <Link underline="hover" color="inherit" href="/dashboard">
                    Dashboard
                </Link>
                <Typography color="text.primary">Badges List</Typography>
            </Breadcrumbs>
            {isLoading ? (
                <Loader />
            ) : (
                <DataGridPremium
                    rows={data}
                    columns={columns.concat(actionColumn)}
                    getRowId={(row) => row.id}  // Updated to use `id`
                    slots={{
                        toolbar: () => (
                            <CustomToolbar
                                EXPORT_FILENAME={EXPORT_FILENAME}
                                onClickCreate={() => {
                                    navigate(BASE_PATH_TO_ADD_BADGES);
                                }}
                                displayCreate={""}
                                onClickUpdate={() => { }}
                                displayUpdate={"none"}
                            />
                        ),
                    }}
                    pagination
                    rowHeight={150}
                    disableRowSelectionOnClick
                    checkboxSelection
                />
            )}
        </div>
    );
};

export default BadgesList;
